import React, { useEffect, useState, useMemo } from 'react';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import ApproveReviewModal from './ApproveReviewModal';

const PAGE_SIZE = 25;

// Star Rating Component
const StarRating = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="flex items-center">
      {[...Array(fullStars)].map((_, i) => (
        <StarIcon key={`full-${i}`} className="h-4 w-4 text-yellow-400" />
      ))}
      {hasHalfStar && (
        <div className="relative h-4 w-4">
          <StarOutlineIcon className="h-4 w-4 text-yellow-400" />
          <StarIcon className="h-4 w-4 text-yellow-400 absolute inset-0" style={{ clipPath: 'inset(0 50% 0 0)' }} />
        </div>
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <StarOutlineIcon key={`empty-${i}`} className="h-4 w-4 text-yellow-400" />
      ))}
    </div>
  );
};

const PendingReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [userAvatars, setUserAvatars] = useState({}); // userid -> { avatar, firstname, lastname }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'pending' or 'approved' or 'all'

  // Fetch reviews and user avatars
  useEffect(() => {
    const fetchReviewsAndAvatars = async () => {
      setLoading(true);
      setError(null);
      try {
        const db = getFirestore();
        let q;
        
        if (statusFilter === 'pending') {
          q = query(collection(db, 'reviews'), where('approved', '==', false));
        } else if (statusFilter === 'approved') {
          q = query(collection(db, 'reviews'), where('approved', '==', true));
        } else {
          // 'all' - fetch all reviews
          q = query(collection(db, 'reviews'));
        }
        
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setReviews(data);
        // Fetch avatars for all unique userids
        const userIds = Array.from(new Set(data.map(r => r.userid).filter(Boolean)));
        if (userIds.length > 0) {
          const usersRef = collection(db, 'users');
          // Firestore doesn't support 'in' with too many items, so chunk if needed
          const chunkSize = 10;
          let avatarMap = {};
          for (let i = 0; i < userIds.length; i += chunkSize) {
            const chunk = userIds.slice(i, i + chunkSize);
            const userQ = query(usersRef, where('__name__', 'in', chunk));
            const userSnap = await getDocs(userQ);
            userSnap.forEach(userDoc => {
              const d = userDoc.data();
              avatarMap[userDoc.id] = {
                avatar: d.avatar || d.profilePicture || '',
                firstname: d.firstname || d.firstName || '',
                lastname: d.lastname || d.lastName || ''
              };
            });
          }
          setUserAvatars(avatarMap);
        } else {
          setUserAvatars({});
        }
      } catch (err) {
        setError('Failed to fetch reviews.');
      } finally {
        setLoading(false);
      }
    };
    fetchReviewsAndAvatars();
  }, [statusFilter]);

  // Filter reviews by search
  const filteredReviews = useMemo(() => {
    if (!search.trim()) return reviews;
    const s = search.toLowerCase();
    return reviews.filter(r =>
      (r.spacename || '').toLowerCase().includes(s) ||
      (r.username || '').toLowerCase().includes(s) ||
      (r.city || '').toLowerCase().includes(s)
    );
  }, [reviews, search]);

  // Pagination
  const totalPages = Math.ceil(filteredReviews.length / PAGE_SIZE);
  const paginatedReviews = filteredReviews.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Multi-select logic
  const toggleSelect = (id) => {
    setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };
  const selectAllOnPage = () => {
    const ids = paginatedReviews.map(r => r.id);
    setSelectedIds(prev => {
      const allSelected = ids.every(id => prev.includes(id));
      return allSelected ? prev.filter(id => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]));
    });
  };
  const allSelectedOnPage = paginatedReviews.length > 0 && paginatedReviews.every(r => selectedIds.includes(r.id));

  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    setBulkLoading(true);
    try {
      const db = getFirestore();
      await Promise.all(selectedIds.map(id => updateDoc(doc(db, 'reviews', id), { approved: true })));
      // Refresh reviews based on current filter
      let q;
      if (statusFilter === 'pending') {
        q = query(collection(db, 'reviews'), where('approved', '==', false));
      } else if (statusFilter === 'approved') {
        q = query(collection(db, 'reviews'), where('approved', '==', true));
      } else {
        q = query(collection(db, 'reviews'));
      }
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReviews(data);
      setSelectedIds([]);
    } catch (err) {
      alert('Failed to approve selected reviews.');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleCardClick = (review) => {
    setSelectedReview(review);
    setModalOpen(true);
  };

  const handleModalClose = (refresh) => {
    setModalOpen(false);
    setSelectedReview(null);
    if (refresh) {
      setLoading(true);
      const db = getFirestore();
      let q;
      if (statusFilter === 'pending') {
        q = query(collection(db, 'reviews'), where('approved', '==', false));
      } else if (statusFilter === 'approved') {
        q = query(collection(db, 'reviews'), where('approved', '==', true));
      } else {
        q = query(collection(db, 'reviews'));
      }
      getDocs(q).then(snapshot => {
        setReviews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      });
    }
  };

  return (
    <div className="p-6 w-full">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {statusFilter === 'pending' ? 'Pending Reviews' : 
         statusFilter === 'approved' ? 'Approved Reviews' : 'All Reviews'}
      </h1>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        {/* Status Filter */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
                setSelectedIds([]);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="pending">Pending Reviews</option>
              <option value="approved">Approved Reviews</option>
              <option value="all">All Reviews</option>
            </select>
          </div>
        </div>
        
        <input
          type="text"
          placeholder="Search by space name, user name, or city..."
          className="block w-full border border-gray-300 rounded-md px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        {selectedIds.length > 0 && statusFilter === 'pending' && (
          <button
            className="mb-4 px-4 py-2 bg-blue-700 text-white rounded font-semibold hover:bg-blue-800 disabled:opacity-50"
            onClick={handleBulkApprove}
            disabled={bulkLoading}
          >
            {bulkLoading ? 'Approving...' : `Approve Selected (${selectedIds.length})`}
          </button>
        )}
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-500">Loading...</span>
          </div>
        ) : error ? (
          <div className="text-red-600 py-4">{error}</div>
        ) : (
          <div className="space-y-4">
            {paginatedReviews.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                {statusFilter === 'pending' ? 'No pending reviews found.' :
                 statusFilter === 'approved' ? 'No approved reviews found.' :
                 'No reviews found.'}
              </div>
            ) : (
              <>
                {statusFilter === 'pending' && (
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      onChange={selectAllOnPage}
                      className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Select all on this page</span>
                  </div>
                )}
                {paginatedReviews.map(review => {
                  const user = userAvatars[review.userid] || {};
                  const isPending = !review.approved;
                  return (
                    <div
                      key={review.id}
                      className={`flex items-start bg-gray-50 border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition cursor-pointer gap-4 relative ${
                        review.approved ? 'border-green-200 bg-green-50' : ''
                      }`}
                    >
                      {isPending && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(review.id)}
                          onChange={() => toggleSelect(review.id)}
                          className="absolute left-2 top-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                      <div className="flex-shrink-0">
                        {user.avatar ? (
                          <img src={user.avatar} alt="avatar" className="w-12 h-12 rounded-full object-cover border border-gray-200" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-lg font-bold text-blue-700">
                            {user.firstname?.charAt(0) || review.username?.charAt(0) || 'U'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1" onClick={() => handleCardClick(review)}>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <div>
                            <div className="text-base font-semibold text-gray-900 mb-1">{review.spacename}</div>
                            <div className="text-sm text-gray-600 mb-1">{review.spaceaddr}</div>
                            <div className="text-sm text-gray-600">User: <span className="font-medium text-gray-800">{review.username}</span></div>
                          </div>
                          <div className="flex flex-col md:items-end gap-1 min-w-[120px]">
                            <div className="text-sm text-gray-600 flex items-center gap-2">
                              Rate: <span className="font-semibold text-blue-700">{review.rate}</span>
                              <StarRating rating={parseFloat(review.rate) || 0} />
                            </div>
                            <div className="text-sm text-gray-600">{review.date ? format(review.date.toDate ? review.date.toDate() : new Date(review.date), 'MMM dd, yyyy') : ''}</div>
                            <div className="text-xs">
                              <span className={`inline-flex px-2 py-1 rounded-full font-semibold ${
                                review.approved 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {review.approved ? 'Approved' : 'Pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="text-sm font-semibold text-gray-700 mb-1">Review Comments</div>
                          <div className="text-lg font-bold text-blue-900 break-words" title={review.review}>{review.review}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center pt-4">
                <div className="text-sm text-gray-600">
                  Page {page} of {totalPages || 1}
                </div>
                <div className="space-x-2">
                  <button
                    className="px-3 py-1 rounded border text-sm disabled:opacity-50"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >Prev</button>
                  <button
                    className="px-3 py-1 rounded border text-sm disabled:opacity-50"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || totalPages === 0}
                  >Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Modal for Approve/Delete */}
      {modalOpen && selectedReview && (
        <ApproveReviewModal
          review={selectedReview}
          open={modalOpen}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default PendingReviews; 